import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid, Switch,
  TextField, IconButton, Chip, CircularProgress, Alert, InputAdornment,
  Divider, Paper, List, ListItem, ListItemAvatar, Avatar, ListItemText, ListItemSecondaryAction,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as MoveLeftIcon,
  ArrowForward as MoveRightIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  PhoneIphone as PhoneIcon,
  Headphones as AudioIcon,
  TabletMac as TabletIcon,
  ViewAgenda as SectionIcon,
  LocalOffer as DealsIcon,
  Storefront as StoreIcon,
  ElectricBolt as FlashIcon,
  Image as ImageIcon,
  FiberManualRecord as DotIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { color, shadow } from '../theme/tokens';
import { useFeedback } from '../components/ui';
import { getImageUrl } from '../config';

const SECTION_CONFIG = {
  hero: {
    icon: <ImageIcon fontSize="small" />,
    badge: 'SLOT BD / BM',
    wireframeLabel: 'Top Hero Rotating Slider (1920×400px)',
    bannerTab: 'BD'
  },
  categories: {
    icon: <SectionIcon fontSize="small" />,
    badge: 'SYSTEM NAV',
    wireframeLabel: '7 Circular Category Circles & Deals Link',
  },
  promo_grid: {
    icon: <DealsIcon fontSize="small" />,
    badge: 'SLOT SG',
    wireframeLabel: 'Dual Promo Cards (Below Categories)',
    bannerTab: 'SG'
  },
  shelf_phones: {
    icon: <PhoneIcon fontSize="small" />,
    badge: 'PRODUCT RAIL #1',
    wireframeLabel: 'Best-Selling Phones (Curated 4 Items)',
    category: 'phones'
  },
  dock: {
    icon: <FlashIcon fontSize="small" />,
    badge: 'LIVE PROMISE',
    wireframeLabel: '90-Min Delivery Dial & 30-Min Pickup Dock',
  },
  spotlight_strip: {
    icon: <DealsIcon fontSize="small" />,
    badge: 'SLOT HPB',
    wireframeLabel: 'Deals & Flash Offer Carousel Strip (1200×240px)',
    bannerTab: 'HPB'
  },
  story: {
    icon: <FlashIcon fontSize="small" />,
    badge: 'BRAND STORY',
    wireframeLabel: '90-Minute Express Hyperlocal Story Cards',
  },
  shelf_audio: {
    icon: <AudioIcon fontSize="small" />,
    badge: 'PRODUCT RAIL #2',
    wireframeLabel: 'Earbuds, Speakers & Smartwatches (Curated 5 Items)',
    category: 'audio'
  },
  featured_phone: {
    icon: <StarIcon fontSize="small" />,
    badge: 'HERO SHOWCASE',
    wireframeLabel: 'Flagship Cinematic Dark Launch Showcase',
    category: 'phones'
  },
  brand_showcase: {
    icon: <StoreIcon fontSize="small" />,
    badge: 'SLOT HPG',
    wireframeLabel: 'Authorized Brand Partner Spotlight Tiles',
    bannerTab: 'HPG'
  },
  shelf_tablets: {
    icon: <TabletIcon fontSize="small" />,
    badge: 'PRODUCT RAIL #3',
    wireframeLabel: 'Tablets & High-Speed Chargers (Curated 5 Items)',
    category: 'tablets'
  },
  stores: {
    icon: <StoreIcon fontSize="small" />,
    badge: 'STORE LOCATOR',
    wireframeLabel: 'Store Pickup Locator & Interactive Store Finder',
  }
};

export default function HomepageCustomizer() {
  const navigate = useNavigate();
  const { notify } = useFeedback();

  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  // Search states for product shelves
  const [searchQuery, setSearchQuery] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [searchLoading, setSearchLoading] = useState({});

  // Active navigation anchor
  const [activeAnchor, setActiveAnchor] = useState('hero');

  // Load layout from backend
  const fetchLayout = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/admin/homepage/layout');
      if (res.data?.success) {
        setSections(res.data.data);
      }
    } catch (err) {
      notify('Failed to load homepage layout', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchLayout();
  }, [fetchLayout]);

  // Product search helper with debounce
  const handleSearchProduct = async (sectionKey, query, category = '') => {
    setSearchQuery(prev => ({ ...prev, [sectionKey]: query }));
    if (!query || query.trim().length < 2) {
      setSearchResults(prev => ({ ...prev, [sectionKey]: [] }));
      return;
    }

    setSearchLoading(prev => ({ ...prev, [sectionKey]: true }));
    try {
      const res = await api.get('/api/admin/homepage/search-products', {
        params: { q: query.trim(), category, limit: 8 }
      });
      if (res.data?.success) {
        setSearchResults(prev => ({ ...prev, [sectionKey]: res.data.data }));
      }
    } catch (err) {
      // Ignore debounce/abort
    } finally {
      setSearchLoading(prev => ({ ...prev, [sectionKey]: false }));
    }
  };

  // Add product to section
  const handleAddProduct = async (sectionKey, product) => {
    const section = sections.find(s => s.section_key === sectionKey);
    if (!section) return;

    const currentIds = Array.isArray(section.curated_product_ids) ? [...section.curated_product_ids] : [];
    if (currentIds.includes(product.id)) {
      notify('Product is already in this shelf', 'warning');
      return;
    }

    const nextIds = sectionKey === 'featured_phone' ? [product.id] : [...currentIds, product.id];
    const nextItems = sectionKey === 'featured_phone' ? [product] : [...(section.items || []), product];

    // Optimistically update local UI
    setSections(prev => prev.map(s => s.section_key === sectionKey ? {
      ...s,
      curated_product_ids: nextIds,
      items: nextItems
    } : s));

    // Clear search
    setSearchQuery(prev => ({ ...prev, [sectionKey]: '' }));
    setSearchResults(prev => ({ ...prev, [sectionKey]: [] }));

    await saveSection(sectionKey, { curated_product_ids: nextIds });
  };

  // Remove product from section
  const handleRemoveProduct = async (sectionKey, productId) => {
    const section = sections.find(s => s.section_key === sectionKey);
    if (!section) return;

    const nextIds = (section.curated_product_ids || []).filter(id => id !== productId);
    const nextItems = (section.items || []).filter(item => item.id !== productId);

    setSections(prev => prev.map(s => s.section_key === sectionKey ? {
      ...s,
      curated_product_ids: nextIds,
      items: nextItems
    } : s));

    await saveSection(sectionKey, { curated_product_ids: nextIds });
  };

  // Move product position
  const handleMoveProduct = async (sectionKey, index, direction) => {
    const section = sections.find(s => s.section_key === sectionKey);
    if (!section || !section.items) return;

    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= section.items.length) return;

    const nextIds = [...section.curated_product_ids];
    const nextItems = [...section.items];

    const tempId = nextIds[index];
    nextIds[index] = nextIds[targetIdx];
    nextIds[targetIdx] = tempId;

    const tempItem = nextItems[index];
    nextItems[index] = nextItems[targetIdx];
    nextItems[targetIdx] = tempItem;

    setSections(prev => prev.map(s => s.section_key === sectionKey ? {
      ...s,
      curated_product_ids: nextIds,
      items: nextItems
    } : s));

    await saveSection(sectionKey, { curated_product_ids: nextIds });
  };

  // Toggle active status
  const handleToggleActive = async (sectionKey, currentStatus) => {
    const nextStatus = !currentStatus;
    setSections(prev => prev.map(s => s.section_key === sectionKey ? { ...s, is_active: nextStatus } : s));
    await saveSection(sectionKey, { is_active: nextStatus });
  };

  // Save section configuration
  const saveSection = async (sectionKey, payload = {}) => {
    setSavingKey(sectionKey);
    try {
      const section = sections.find(s => s.section_key === sectionKey);
      const data = {
        title: section?.title,
        subtitle: section?.subtitle,
        is_active: section?.is_active,
        curation_mode: section?.curation_mode,
        curated_product_ids: section?.curated_product_ids,
        banner_position: section?.banner_position,
        ...payload
      };

      const res = await api.put(`/api/admin/homepage/layout/section/${sectionKey}`, data);
      if (res.data?.success) {
        notify(`Updated ${section?.title || sectionKey}`, 'success');
      }
    } catch (err) {
      notify(`Failed to update ${sectionKey}`, 'error');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 }, maxWidth: 1440, mx: 'auto' }}>
      {/* ── Top Header ── */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { md: 'center' }, gap: 2, mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: color.primary[600], letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Marketing & CMS
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#ECFDF5', px: 1, py: 0.2, borderRadius: '6px', border: '1px solid #A7F3D0' }}>
              <DotIcon sx={{ fontSize: 10, color: '#059669' }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#065F46', fontSize: 11 }}>
                Live Storefront Synced
              </Typography>
            </Box>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: color.ink[900], letterSpacing: '-0.02em' }}>
            Homepage Layout & Curation
          </Typography>
          <Typography variant="body2" sx={{ color: color.ink[500], mt: 0.25 }}>
            Manage what products, flagship spotlights, and promotional banners appear in every section of the live storefront scroll-by-scroll.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon fontSize="small" />}
            onClick={fetchLayout}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              borderColor: color.ink[200],
              color: color.ink[700],
              bgcolor: '#FFFFFF',
              '&:hover': { bgcolor: color.ink[50], borderColor: color.ink[300] }
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" />}
            onClick={() => window.open('http://localhost:5173/', '_blank')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.85rem',
              backgroundColor: color.primary[600],
              '&:hover': { backgroundColor: color.primary[700] },
              boxShadow: shadow.sm,
              px: 2,
              py: 0.8
            }}
          >
            View Live Homepage ↗
          </Button>
        </Box>
      </Box>

      {/* ── Interactive Scroll Timeline Navigation Strip ── */}
      <Paper
        elevation={0}
        sx={{
          p: 1.2,
          mb: 3.5,
          borderRadius: '12px',
          border: `1px solid ${color.ink[200]}`,
          backgroundColor: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 800, color: color.ink[400], px: 1, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.72rem' }}>
          Scroll Map:
        </Typography>
        {sections.map((s, idx) => {
          const isSelected = activeAnchor === s.section_key;
          return (
            <Button
              key={s.section_key}
              size="small"
              onClick={() => {
                setActiveAnchor(s.section_key);
                document.getElementById(`section-${s.section_key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
              sx={{
                textTransform: 'none',
                fontWeight: isSelected ? 700 : 500,
                fontSize: '0.78rem',
                borderRadius: '8px',
                px: 1.4,
                py: 0.5,
                backgroundColor: isSelected ? color.ink[900] : 'transparent',
                color: isSelected ? '#FFFFFF' : s.is_active ? color.ink[700] : color.ink[400],
                border: `1px solid ${isSelected ? color.ink[900] : color.ink[200]}`,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                '&:hover': { backgroundColor: isSelected ? color.ink[800] : color.ink[100] }
              }}
            >
              <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 800, opacity: isSelected ? 0.8 : 0.5 }}>
                #{idx + 1}
              </Box>
              <span>{s.title.split(' ')[0]}</span>
              {!s.is_active && (
                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#9CA3AF' }} />
              )}
            </Button>
          );
        })}
      </Paper>

      {/* ── Main Layout View ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
          <CircularProgress size={36} sx={{ color: color.primary[600] }} />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {sections.map((section, index) => {
            const cfg = SECTION_CONFIG[section.section_key] || {};
            const isProductShelf = ['shelf_phones', 'shelf_audio', 'shelf_tablets'].includes(section.section_key);
            const isFeaturedPhone = section.section_key === 'featured_phone';
            const isBannerSection = ['hero', 'promo_grid', 'spotlight_strip', 'brand_showcase'].includes(section.section_key);
            const isSaving = savingKey === section.section_key;

            return (
              <Card
                key={section.section_key}
                id={`section-${section.section_key}`}
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  border: `1px solid ${section.is_active ? '#E5E7EB' : '#F3F4F6'}`,
                  backgroundColor: section.is_active ? '#FFFFFF' : '#F9FAFB',
                  boxShadow: section.is_active ? '0 1px 3px rgba(0,0,0,0.03)' : 'none',
                  transition: 'all 0.2s ease',
                  overflow: 'visible'
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.75 } }}>
                  {/* ── Section Header Row ── */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: section.is_active ? color.primary[50] : color.ink[100],
                          color: section.is_active ? color.primary[600] : color.ink[400],
                          fontWeight: 800,
                          fontSize: '0.8rem',
                          fontFamily: 'monospace'
                        }}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: section.is_active ? color.ink[900] : color.ink[400], fontSize: '1rem' }}>
                            {section.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={cfg.badge || 'HOMEPAGE'}
                            sx={{ height: 20, fontSize: 10, fontWeight: 700, backgroundColor: color.ink[100], color: color.ink[700] }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: color.ink[500], fontSize: '0.8rem', display: 'block', mt: 0.25 }}>
                          {section.subtitle || cfg.wireframeLabel}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Active Toggle */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {isSaving && <CircularProgress size={16} sx={{ color: color.primary[600] }} />}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.25,
                          py: 0.4,
                          borderRadius: '20px',
                          border: `1px solid ${section.is_active ? '#D1FAE5' : '#E5E7EB'}`,
                          bgcolor: section.is_active ? '#ECFDF5' : '#F9FAFB'
                        }}
                      >
                        <DotIcon sx={{ fontSize: 10, color: section.is_active ? '#10B981' : '#9CA3AF' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: section.is_active ? '#065F46' : color.ink[500], fontSize: '0.75rem' }}>
                          {section.is_active ? 'Live' : 'Hidden'}
                        </Typography>
                        <Switch
                          size="small"
                          checked={section.is_active}
                          onChange={() => handleToggleActive(section.section_key, section.is_active)}
                          color="success"
                          sx={{ '& .MuiSwitch-thumb': { width: 14, height: 14 }, ml: 0.5 }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.75, borderColor: '#F3F4F6' }} />

                  {/* ── Case 1: Product Shelves (Best-Selling Phones, Audio, Tablets) ── */}
                  {isProductShelf && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: color.ink[600], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Curated Display Items ({(section.items || []).length} Products)
                        </Typography>
                        <Typography variant="caption" sx={{ color: color.ink[400] }}>
                          Use arrow buttons to adjust storefront display order
                        </Typography>
                      </Box>

                      {/* Product Cards Row */}
                      <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        {(section.items || []).map((item, itemIdx) => (
                          <Grid item xs={12} sm={6} md={3} key={item.id}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                borderRadius: '10px',
                                borderColor: '#E5E7EB',
                                bgcolor: '#FFFFFF',
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                transition: 'all 0.15s ease',
                                '&:hover': { borderColor: color.primary[300], boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }
                              }}
                            >
                              <Box
                                component="img"
                                src={getImageUrl(item.primaryImage)}
                                alt={item.name}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  objectFit: 'contain',
                                  borderRadius: '6px',
                                  bgcolor: '#F9FAFB',
                                  border: '1px solid #F3F4F6',
                                  p: 0.5
                                }}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = getImageUrl('/uploads/products/iphone-17-1.webp');
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: color.primary[600], fontSize: '0.68rem', textTransform: 'uppercase' }}>
                                  #{itemIdx + 1} {item.brand}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: color.ink[900], fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {item.name}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: color.ink[800], fontSize: '0.8rem' }}>
                                  ₹{item.salePrice?.toLocaleString('en-IN')}
                                </Typography>
                              </Box>

                              {/* Controls */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <IconButton
                                  size="small"
                                  disabled={itemIdx === 0}
                                  onClick={() => handleMoveProduct(section.section_key, itemIdx, -1)}
                                  sx={{ p: 0.5, color: color.ink[500], '&:hover': { color: color.ink[900] } }}
                                  title="Move earlier"
                                >
                                  <MoveLeftIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  disabled={itemIdx === (section.items?.length - 1)}
                                  onClick={() => handleMoveProduct(section.section_key, itemIdx, 1)}
                                  sx={{ p: 0.5, color: color.ink[500], '&:hover': { color: color.ink[900] } }}
                                  title="Move later"
                                >
                                  <MoveRightIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveProduct(section.section_key, item.id)}
                                  sx={{ p: 0.5, color: '#EF4444', '&:hover': { bgcolor: '#FEE2E2' } }}
                                  title="Remove from shelf"
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>

                      {/* Product Search & Quick Add */}
                      <Box sx={{ position: 'relative', maxWidth: 640 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={`Add product to ${section.title}... (e.g. iPhone, Samsung, AirPods)`}
                          value={searchQuery[section.section_key] || ''}
                          onChange={(e) => handleSearchProduct(section.section_key, e.target.value, cfg.category || '')}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {searchLoading[section.section_key] ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" sx={{ color: color.ink[400] }} />}
                              </InputAdornment>
                            ),
                            sx: {
                              borderRadius: '8px',
                              bgcolor: '#FFFFFF',
                              fontSize: '0.85rem',
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' }
                            }
                          }}
                        />

                        {/* Search Dropdown Results */}
                        {searchResults[section.section_key]?.length > 0 && (
                          <Paper
                            elevation={4}
                            sx={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 20,
                              mt: 0.5,
                              maxHeight: 280,
                              overflowY: 'auto',
                              borderRadius: '8px',
                              border: `1px solid ${color.ink[200]}`
                            }}
                          >
                            <List dense disablePadding>
                              {searchResults[section.section_key].map((p) => (
                                <ListItem
                                  key={p.id}
                                  button
                                  onClick={() => handleAddProduct(section.section_key, p)}
                                  sx={{ borderBottom: `1px solid #F3F4F6`, '&:hover': { backgroundColor: color.primary[50] } }}
                                >
                                  <ListItemAvatar>
                                    <Avatar
                                      src={getImageUrl(p.primaryImage)}
                                      variant="rounded"
                                      sx={{ width: 34, height: 34, backgroundColor: '#F9FAFB' }}
                                    />
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={<Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{p.name}</Typography>}
                                    secondary={`${p.brand || 'Hotspot'} • ₹${p.salePrice?.toLocaleString('en-IN')}`}
                                  />
                                  <ListItemSecondaryAction>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      startIcon={<AddIcon fontSize="small" />}
                                      onClick={() => handleAddProduct(section.section_key, p)}
                                      sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        backgroundColor: color.primary[600],
                                        '&:hover': { backgroundColor: color.primary[700] },
                                        py: 0.25,
                                        px: 1.25
                                      }}
                                    >
                                      Add
                                    </Button>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ))}
                            </List>
                          </Paper>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* ── Case 2: Featured Flagship Showcase (Cinematic Hero Product) ── */}
                  {isFeaturedPhone && (
                    <Box>
                      <Grid container spacing={2.5} alignItems="center">
                        <Grid item xs={12} md={7}>
                          {section.items?.[0] ? (
                            <Box
                              sx={{
                                p: 2.2,
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
                                color: '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2.5,
                                border: '1px solid rgba(255,255,255,0.08)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                              }}
                            >
                              <Box
                                component="img"
                                src={getImageUrl(section.items[0].primaryImage)}
                                alt={section.items[0].name}
                                sx={{
                                  width: 84,
                                  height: 84,
                                  objectFit: 'contain',
                                  borderRadius: '8px',
                                  backgroundColor: 'rgba(255,255,255,0.05)',
                                  p: 1
                                }}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = getImageUrl('/uploads/products/iphone-17-1.webp');
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Chip
                                  size="small"
                                  label="CURRENT HERO FLAGSHIP"
                                  sx={{ height: 18, fontSize: 9, fontWeight: 800, backgroundColor: color.primary[600], color: '#FFFFFF', mb: 0.5 }}
                                />
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#FFFFFF', lineHeight: 1.2 }} noWrap>
                                  {section.items[0].name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mt: 0.25 }}>
                                  Featured starting from <b style={{ color: '#FECF05' }}>₹{section.items[0].salePrice?.toLocaleString('en-IN')}</b>
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mt: 0.25, fontSize: '0.72rem' }}>
                                  Delivered with Hotspot Express 90-min guarantee & official brand warranty
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Alert severity="warning">No flagship phone selected. Default device will show.</Alert>
                          )}
                        </Grid>

                        <Grid item xs={12} md={5}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: color.ink[600], display: 'block', mb: 0.5 }}>
                            Replace Flagship Device:
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Search flagship phone (e.g. iPhone 16 Pro, S24 Ultra)..."
                            value={searchQuery[section.section_key] || ''}
                            onChange={(e) => handleSearchProduct(section.section_key, e.target.value, 'phones')}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  {searchLoading[section.section_key] ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" sx={{ color: color.ink[400] }} />}
                                </InputAdornment>
                              ),
                              sx: { borderRadius: '8px', bgcolor: '#FFFFFF', fontSize: '0.85rem' }
                            }}
                          />

                          {searchResults[section.section_key]?.length > 0 && (
                            <Paper
                              elevation={4}
                              sx={{
                                position: 'absolute',
                                zIndex: 20,
                                mt: 0.5,
                                maxWidth: 440,
                                maxHeight: 240,
                                overflowY: 'auto',
                                borderRadius: '8px',
                                border: `1px solid ${color.ink[200]}`
                              }}
                            >
                              <List dense disablePadding>
                                {searchResults[section.section_key].map((p) => (
                                  <ListItem
                                    key={p.id}
                                    button
                                    onClick={() => handleAddProduct(section.section_key, p)}
                                    sx={{ borderBottom: `1px solid #F3F4F6`, '&:hover': { backgroundColor: color.primary[50] } }}
                                  >
                                    <ListItemAvatar>
                                      <Avatar src={getImageUrl(p.primaryImage)} variant="rounded" sx={{ width: 32, height: 32 }} />
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={<Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{p.name}</Typography>}
                                      secondary={`₹${p.salePrice?.toLocaleString('en-IN')}`}
                                    />
                                    <ListItemSecondaryAction>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => handleAddProduct(section.section_key, p)}
                                        sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', backgroundColor: color.primary[600] }}
                                      >
                                        Set Flagship
                                      </Button>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* ── Case 3: Banner Placement Sections (Hero BD, Promo SG, Deals HPB, Brands HPG) ── */}
                  {isBannerSection && (
                    <Box>
                      {/* Active Banners Visual Strip */}
                      {section.banners && section.banners.length > 0 ? (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: color.ink[500], display: 'block', mb: 1, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Active Visual Banners In This Slot ({section.banners.length} Live Items):
                          </Typography>
                          <Grid container spacing={1.5}>
                            {section.banners.map((b, bIdx) => (
                              <Grid item xs={12} sm={section.banners.length === 1 ? 12 : 6} md={section.banners.length === 1 ? 6 : section.banners.length === 2 ? 6 : 3} key={b.id || bIdx}>
                                <Box
                                  sx={{
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    border: '1px solid #E5E7EB',
                                    bgcolor: '#111827',
                                    position: 'relative'
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={getImageUrl(b.image || b.path)}
                                    alt={b.title || 'Banner'}
                                    sx={{
                                      width: '100%',
                                      height: section.section_key === 'spotlight_strip' ? 100 : 80,
                                      objectFit: 'cover',
                                      display: 'block'
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = '/uploads/banners/desktop_samsung_galaxy_s26_ultra.webp';
                                    }}
                                  />
                                  <Box sx={{ p: 1, bgcolor: '#FFFFFF', borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: color.ink[800], display: 'block' }} noWrap>
                                        #{bIdx + 1} {b.title || 'Promotional Slide'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: color.ink[400], fontSize: '0.7rem' }} noWrap>
                                        {b.link || '/category/phones'}
                                      </Typography>
                                    </Box>
                                    <Chip size="small" label="Active" color="success" sx={{ height: 16, fontSize: 9, fontWeight: 700 }} />
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      ) : (
                        <Box sx={{ p: 1.5, mb: 1.5, bgcolor: '#F9FAFB', borderRadius: '8px', border: '1px dashed #E5E7EB' }}>
                          <Typography variant="caption" sx={{ color: color.ink[500] }}>
                            No active banners currently uploaded in slot <b>{section.banner_position}</b>. System fallback creative will display.
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          endIcon={<OpenInNewIcon fontSize="small" />}
                          onClick={() => navigate('/banners')}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            borderColor: color.ink[300],
                            color: color.primary[600],
                            bgcolor: '#FFFFFF',
                            '&:hover': { borderColor: color.primary[600], bgcolor: color.primary[50] }
                          }}
                        >
                          Configure {section.title} Banners →
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* ── Case 4: System Experiential Sections (Categories, Dock, Story, Stores) ── */}
                  {!isProductShelf && !isFeaturedPhone && !isBannerSection && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: color.ink[600], fontSize: '0.85rem' }}>
                        Core quick-commerce experiential block. Powered dynamically by store catalog and hyper-local rider dispatch.
                      </Typography>
                      <Chip label="System Block" size="small" variant="outlined" sx={{ fontSize: '0.72rem', height: 20 }} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
